import { LitElement, html } from 'lit';

import { ifDefined } from 'lit/directives/if-defined.js';
import styles from './checkbox.styles';
import { CHANGE_EVENT } from './checkbox.constants';

import '../icon';

export interface CheckboxProps {
	id?: string;
	name?: string;
	disabled?: boolean;
	indeterminate?: boolean;
	checked?: boolean;
	'm-change'?: (
		event: CustomEvent<{ checked: boolean; indeterminate: boolean }>,
	) => void;
}

export class Checkbox extends LitElement {
	static override styles = [styles()];

	static override get properties() {
		return {
			name: { type: String },
			disabled: { type: Boolean, reflect: true },
			checked: { type: Boolean, reflect: true },
			indeterminate: { type: Boolean, reflect: true },
		};
	}

	constructor() {
		super();

		this.name = undefined;
		this.disabled = false;
		this.checked = false;
		this.indeterminate = false;
	}

	/**
	 * Id of the checkbox input.
	 */
	declare id: string;

	/**
	 * Name of the checkbox input.
	 */
	declare name: CheckboxProps['name'];

	/**
	 * Option to disable the checkbox.
	 */
	declare disabled: boolean;

	/**
	 * Option to disable the checkbox.
	 */
	declare checked: boolean;

	/**
	 * Option to set the checkbox as indeterminate.
	 */
	declare indeterminate: boolean;

	/**
	 * Input element within component.
	 */
	private declare input: HTMLInputElement;

	/**
	 * Label element within component.
	 */
	private declare label: HTMLLabelElement;

	override connectedCallback(): void {
		super.connectedCallback();

		this.setAttribute('role', 'checkbox');
	}

	override disconnectedCallback(): void {
		super.connectedCallback();
	}

	override firstUpdated(): void {
		this.input = this.renderRoot.querySelector('input')!;
		this.label = this.renderRoot.querySelector('label')!;
	}

	override updated(): void {
		this.#setAttributes();
	}

	override render() {
		return html`
			<label for="${ifDefined(this.id)}" class="check-input-label">
				<input
					id="${ifDefined(this.id)}"
					name="${ifDefined(this.name)}"
					type="checkbox"
					class="check-input"
					?disabled="${this.disabled}"
					?checked="${this.checked}"
					@change=${this.#handleChange}
				/>
				<m-icon name="check" class="check-icon"></m-icon>
				<m-icon name="indeterminate" class="indeterminate-icon"></m-icon>
				<slot name="leading"></slot>
				<slot></slot>
			</label>
		`;
	}

	#setAttributes(): void {
		this.setAttribute('aria-disabled', this.disabled.toString());
		this.setAttribute(
			'aria-checked',
			this.indeterminate ? 'mixed' : this.checked.toString(),
		);

		this.input.indeterminate = this.indeterminate;
	}

	#handleChange = (event: Event) => {
		const checkboxInput = event.target as HTMLInputElement;
		const customEvent = new CustomEvent(CHANGE_EVENT, {
			cancelable: false,
			composed: false,
			bubbles: true,
			detail: {
				checked: checkboxInput.checked,
				indeterminate: checkboxInput.indeterminate,
			},
		});

		this.dispatchEvent(customEvent);

		this.checked = checkboxInput.checked;
		this.indeterminate = checkboxInput.indeterminate;
	};
}

window.customElements.define('m-checkbox', Checkbox);

declare global {
	interface HTMLElementTagNameMap {
		'm-checkbox': Checkbox;
	}
}
